import * as Sentry from "@sentry/vue";
import cloneDeepWith from "lodash/cloneDeepWith";

export interface ResilientStore {
	getItem<T>(key: string): Promise<T>;
	setItem<T>(key: string, value: T): Promise<T>;
	removeItem(key: string): Promise<void>;
	clear(): Promise<void>;
}

// Deep copy like IndexedDB's structured clone. Blob/File are immutable and lodash would turn them
// into {}, so they are kept as they are
const snapshot = <T>(value: T): T => cloneDeepWith(value, item => (item instanceof Blob ? item : undefined));

// IndexedDB calls can fail for a whole page load (corrupt profile, full disk, evicted storage:
// "Internal error opening backing store", "The transaction was aborted") or one call at a time
// (quota on a large draft). Instead of rejecting into Sentry (VUE-161), a change IndexedDB could
// not apply is kept in memory and read back before IndexedDB, so the page still sees its own
// writes and removals while entries IndexedDB holds stay readable.
export function createResilientStore(idb: LocalForage, storeName: string): ResilientStore {
	// Changes IndexedDB failed to apply; null marks a removed key, like localforage's miss.
	// Values are deep copies: IndexedDB stores a snapshot, and form-draft dirty checks compare
	// the cached object with the live one
	const overlay = new Map<string, unknown>();
	// IndexedDB cannot be used for the rest of the page load (localforage keeps ready() rejected
	// once the database failed to open): the overlay is the whole store
	let memoryOnly = false;
	// IndexedDB may still hold entries a clear() removed: set when a clear starts and reset only
	// when the latest clear succeeds. Reads skip IndexedDB meanwhile, and writes keep going to
	// IndexedDB but are also read back from memory
	let staleIdb = false;
	let clears = 0;
	const reported = new Set<string>();
	// Every set/remove takes a number per key, and its result only touches the overlay if no newer
	// set/remove/clear for that key started since. Callers fire writes without awaiting them, and a
	// late result (localforage delays Blob writes behind an extra transaction) must not shadow a
	// newer value
	let sequence = 0;
	const latest = new Map<string, number>();

	function track(key: string): () => boolean {
		const mine = ++sequence;
		latest.set(key, mine);
		return () => latest.get(key) === mine;
	}

	function report(message: string, failure: Partial<DOMException> | null | undefined): void {
		if (reported.has(message)) {
			return;
		}
		reported.add(message);
		Sentry.addBreadcrumb({
			category: "storage",
			level: "warning",
			message,
			data: { store: storeName, error: `${failure?.name}: ${failure?.message}` }
		});
	}

	// Runs an IndexedDB call; a failure answers with onFailure and tells an unusable database
	// (ready() rejected) apart from a single failed call
	function attempt<R>(fromIdb: () => Promise<R>, onFailure: () => R): Promise<R> {
		return fromIdb().catch((error: unknown) => {
			// localforage rejects with the IDBRequest's DOMException; read it by shape because
			// instanceof Error is not reliable for DOMException across browsers and realms
			const failure = error as Partial<DOMException> | null | undefined;
			// A value IndexedDB cannot clone is a caller bug, not an unavailable store
			if (failure?.name === "DataCloneError") {
				throw error;
			}
			return idb.ready().then(() => true, () => false).then(databaseOpen => {
				if (!databaseOpen) {
					memoryOnly = true;
				}
				report(databaseOpen ? "IndexedDB call failed, keeping the change in memory" : "IndexedDB unavailable, using in-memory cache", failure);
				return onFailure();
			});
		});
	}

	// Resolves asynchronously like an IndexedDB call, and a throw rejects instead of escaping
	const fromMemory = <R>(read: () => R): Promise<R> => new Promise<R>(resolve => resolve(read()));

	return {
		getItem: <T>(key: string) => {
			if (overlay.has(key)) {
				return fromMemory(() => snapshot(overlay.get(key) as T));
			}
			return memoryOnly || staleIdb ? Promise.resolve(null) : attempt(() => idb.getItem<T>(key), () => null);
		},
		setItem: <T>(key: string, value: T) => {
			const isLatest = track(key);
			const keep = () => {
				if (isLatest()) {
					overlay.set(key, value === undefined ? null : snapshot(value));
				}
				return value;
			};
			if (memoryOnly) {
				return fromMemory(keep);
			}
			// Reads of this key answer from memory: show the new value now, not when IndexedDB answers
			const shadowed = staleIdb || overlay.has(key);
			const hadPrevious = overlay.has(key);
			const previous = overlay.get(key);
			if (shadowed) {
				keep();
			}
			return attempt(() => idb.setItem<T>(key, value).then(saved => {
				if (isLatest() && !staleIdb) {
					overlay.delete(key);
				}
				return saved;
			}), keep).catch((error: unknown) => {
				// Only DataCloneError gets here: the value was never stored, so undo the early copy
				if (shadowed && isLatest()) {
					if (hadPrevious) {
						overlay.set(key, previous);
					} else {
						overlay.delete(key);
					}
				}
				throw error;
			});
		},
		removeItem: (key: string) => {
			const isLatest = track(key);
			const forget = () => {
				if (isLatest()) {
					overlay.set(key, null);
				}
			};
			if (memoryOnly) {
				return fromMemory(forget);
			}
			if (staleIdb || overlay.has(key)) {
				forget();
			}
			return attempt(() => idb.removeItem(key).then(() => {
				if (isLatest()) {
					overlay.delete(key);
				}
			}), forget);
		},
		clear: () => {
			// Applied here rather than when IndexedDB answers: earlier set/remove results must not
			// refill the store, and writes started after this call must survive its outcome
			latest.clear();
			overlay.clear();
			if (memoryOnly) {
				return Promise.resolve();
			}
			staleIdb = true;
			const mine = ++clears;
			return attempt(() => idb.clear().then(() => {
				if (mine === clears) {
					staleIdb = false;
				}
			}), () => undefined);
		}
	};
}
